/**
 * @fileoverview ClubOS Registrations page — squad registration management per competition
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import LinearProgress from "@mui/material/LinearProgress";

// ClubOS components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";

// ClubOS examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Mock data
import { registrations, squad } from "data/mockData";

// TODO: Replace with API call when backend is ready
// import { squadService } from "api";

const COMPETITIONS = ["Premier Division", "League Cup", "European"];

const LIMITS = {
  "Premier Division": { total: 25, homegrown: 8, nonEU: 5 },
  "League Cup": { total: 25, homegrown: 6, nonEU: 5 },
  "European": { total: 25, homegrown: 8, nonEU: 5 },
};

const ELIGIBILITY_COLOR = {
  Homegrown: "success",
  EU: "info",
  "Non-EU": "warning",
  "On Loan": "dark",
};

function Registrations() {
  const [activeTab, setActiveTab] = useState(0);

  // TODO: Uncomment when API is ready
  // const [data, setData] = useState(registrations);
  // useEffect(() => { squadService.getRegistrations().then(setData).catch(console.error); }, []);

  const competition = COMPETITIONS[activeTab];
  const compRegs = registrations.filter((r) => r.competition === competition);
  const limits = LIMITS[competition];

  // Derive counts from squad for realistic homegrown calculation
  const homegrownCount = compRegs.filter((r) => r.eligibility === "Homegrown").length;
  const nonEUCount = compRegs.filter((r) => r.eligibility === "Non-EU").length;
  const registeredCount = compRegs.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <SoftTypography variant="h4" fontWeight="bold" color="dark">
            Registrations
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Competition squad registration lists — FC Metropolitan
          </SoftTypography>
        </SoftBox>

        {/* Registration limits summary */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card>
                <SoftBox p={2.5}>
                  <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="text">Total Registered</SoftTypography>
                  <SoftBox display="flex" justifyContent="space-between" mt={1}>
                    <SoftTypography variant="h4" fontWeight="bold">{registeredCount}</SoftTypography>
                    <SoftTypography variant="body2" color="text" alignSelf="flex-end">/ {limits.total}</SoftTypography>
                  </SoftBox>
                  <LinearProgress variant="determinate" value={(registeredCount / limits.total) * 100} sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: "#e9ecef", "& .MuiLinearProgress-bar": { backgroundColor: registeredCount >= limits.total ? "#f44336" : "#17c1e8" } }} />
                  {registeredCount >= limits.total && (
                    <SoftBadge badgeContent="Squad Full" color="error" size="xs" container sx={{ mt: 1 }} />
                  )}
                </SoftBox>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <SoftBox p={2.5}>
                  <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="text">Homegrown</SoftTypography>
                  <SoftBox display="flex" justifyContent="space-between" mt={1}>
                    <SoftTypography variant="h4" fontWeight="bold">{homegrownCount}</SoftTypography>
                    <SoftTypography variant="body2" color="text" alignSelf="flex-end">min. {limits.homegrown}</SoftTypography>
                  </SoftBox>
                  <LinearProgress variant="determinate" value={(homegrownCount / limits.homegrown) * 100} sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: "#e9ecef", "& .MuiLinearProgress-bar": { backgroundColor: homegrownCount < limits.homegrown ? "#f44336" : "#82d616" } }} />
                  {homegrownCount < limits.homegrown && (
                    <SoftBadge badgeContent="Shortfall!" color="error" size="xs" container sx={{ mt: 1 }} />
                  )}
                </SoftBox>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <SoftBox p={2.5}>
                  <SoftTypography variant="caption" fontWeight="bold" textTransform="uppercase" color="text">Non-EU Slots</SoftTypography>
                  <SoftBox display="flex" justifyContent="space-between" mt={1}>
                    <SoftTypography variant="h4" fontWeight="bold">{nonEUCount}</SoftTypography>
                    <SoftTypography variant="body2" color="text" alignSelf="flex-end">max. {limits.nonEU}</SoftTypography>
                  </SoftBox>
                  <LinearProgress variant="determinate" value={(nonEUCount / limits.nonEU) * 100} sx={{ mt: 1, height: 6, borderRadius: 3, backgroundColor: "#e9ecef", "& .MuiLinearProgress-bar": { backgroundColor: nonEUCount >= limits.nonEU ? "#ff9800" : "#17c1e8" } }} />
                </SoftBox>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>

        {/* Registration table */}
        <Card>
          <SoftBox p={3}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                {COMPETITIONS.map((c) => <Tab key={c} label={c} sx={{ textTransform: "none" }} />)}
              </Tabs>
              <SoftButton variant="gradient" color="info" size="small">
                Submit Registration
              </SoftButton>
            </SoftBox>

            {compRegs.length === 0 ? (
              <SoftBox py={4} textAlign="center">
                <SoftTypography variant="body2" color="text">
                  No registration data for {competition}.
                </SoftTypography>
                <SoftButton variant="outlined" color="info" size="small" sx={{ mt: 2 }}>
                  Build Registration List
                </SoftButton>
              </SoftBox>
            ) : (
              <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["#", "Player", "Squad Group", "Eligibility", "Status"].map((h) => (
                      <SoftBox key={h} component="th" sx={{ textAlign: "left", p: 1.5, fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase", color: "text.secondary", borderBottom: "2px solid #e9ecef" }}>
                        {h}
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {compRegs.map((r, i) => (
                    <SoftBox key={i} component="tr" sx={{ "&:hover": { backgroundColor: "#f8f9fa" } }}>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", width: 40 }}>
                        <SoftTypography variant="caption" color="text">{i + 1}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="medium">{r.player}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="caption" color="text">{r.squad}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftBadge badgeContent={r.eligibility} color={ELIGIBILITY_COLOR[r.eligibility]} size="xs" container />
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftBadge badgeContent={r.registered ? "Eligible" : "Not Registered"} color={r.registered ? "success" : "error"} size="xs" container />
                      </SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>
              </SoftBox>
            )}
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: "FC Metropolitan" }} links={[]} />
    </DashboardLayout>
  );
}

export default Registrations;
